/**
 * Alerts Center — /insurer/alerts
 *
 * The full-page triage view for the notifications inbox. The header
 * NotificationBell shows the most recent 20 unread alerts; this page
 * exposes the entire backlog with server-side filters (severity /
 * category / date range / read status) and paged navigation.
 *
 * Bulk actions:
 *   • Mark all as read
 *   • Delete a single alert
 *
 * All rows are click-through — following `url` takes the operator to
 * the record that raised the alert.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input, Label, Select } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Bell, AlertTriangle, ShieldAlert, MailWarning, Info, Check, Trash2,
  RefreshCw, Settings2, X, PackagePlus, Save,
} from 'lucide-react';

interface AlertRow {
  id: string;
  title: string;
  body?: string;
  severity?: 'info' | 'warning' | 'critical' | string;
  category?: string;
  url?: string | null;
  read: boolean;
  created_at: string;
  target_ref?: {
    matrix_id?: string;
    unknown_types?: string[];
    site_id?: string;
    [k: string]: unknown;
  };
}
interface ListPayload {
  items: AlertRow[];
  unread_count: number;
  total: number;
  offset: number;
  limit: number;
}

const PAGE_SIZE = 25;
const SEVERITIES: Array<{ key: string; label: string }> = [
  { key: 'info',     label: 'Info' },
  { key: 'warning',  label: 'Warning' },
  { key: 'critical', label: 'Critical' },
];
const CATEGORIES: Array<{ key: string; label: string }> = [
  { key: 'overdue_survey',    label: 'Overdue surveys' },
  { key: 'critical_site',     label: 'Critical sites' },
  { key: 'delivery_failure',  label: 'Delivery failures' },
  { key: 'unknown_abm',       label: 'Unknown ABM' },
  { key: 'system',            label: 'System' },
];

function severityBadge(sev?: string) {
  const s = (sev || '').toLowerCase();
  if (s === 'critical') return <Badge className="bg-red-600 text-white">Critical</Badge>;
  if (s === 'warning') return <Badge className="bg-amber-500 text-white">Warning</Badge>;
  return <Badge className="bg-raptor-blue/20 text-raptor-navy">Info</Badge>;
}
function categoryIcon(cat?: string) {
  switch ((cat || '').toLowerCase()) {
    case 'delivery_failure': return <MailWarning size={14} />;
    case 'critical_site':    return <ShieldAlert size={14} />;
    case 'overdue_survey':   return <AlertTriangle size={14} />;
    case 'unknown_abm':      return <PackagePlus size={14} />;
    default:                  return <Info size={14} />;
  }
}
function timeFmt(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 19).replace('T', ' ');
}

const AlertsPage: React.FC = () => {
  const nav = useNavigate();
  const [data, setData] = useState<ListPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<{
    severity: string[]; category: string[]; from: string; to: string; unread_only: boolean;
  }>({ severity: [], category: [], from: '', to: '', unread_only: false });
  const [page, setPage] = useState(0);
  // "Add to library" quick-fix modal for unknown_abm alerts
  const [autofix, setAutofix] = useState<AlertRow | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('limit', String(PAGE_SIZE));
      p.set('offset', String(page * PAGE_SIZE));
      if (filters.unread_only) p.set('unread_only', 'true');
      if (filters.severity.length) p.set('severity', filters.severity.join(','));
      if (filters.category.length) p.set('category', filters.category.join(','));
      if (filters.from) p.set('from_date', filters.from);
      if (filters.to)   p.set('to_date', filters.to);
      const r = await api.get<ListPayload>(`/notifications?${p.toString()}`);
      setData(r.data);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Failed to load alerts');
    } finally { setLoading(false); }
  }, [page, filters]);
  useEffect(() => { void load(); }, [load]);

  const toggleValue = useCallback(<K extends 'severity' | 'category'>(key: K, val: string) => {
    setPage(0);
    setFilters(f => {
      const list = f[key];
      return { ...f, [key]: list.includes(val) ? list.filter(x => x !== val) : [...list, val] };
    });
  }, []);

  const markAll = useCallback(async () => {
    try {
      await api.post('/notifications/mark-all-read');
      toast.success('All alerts marked as read');
      await load();
    } catch { toast.error('Failed to mark all read'); }
  }, [load]);

  const markOne = useCallback(async (id: string) => {
    try {
      await api.post(`/notifications/${id}/read`);
      await load();
    } catch { /* ignore */ }
  }, [load]);

  const deleteOne = useCallback(async (id: string) => {
    if (!window.confirm('Delete this alert? This cannot be undone.')) return;
    try {
      await api.delete(`/notifications/${id}`);
      toast.success('Alert deleted');
      await load();
    } catch { toast.error('Delete failed'); }
  }, [load]);

  const openAlert = useCallback(async (n: AlertRow) => {
    if (!n.read) void markOne(n.id);
    if (n.url) nav(n.url);
  }, [markOne, nav]);

  const totalPages = useMemo(() => (data ? Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)) : 1), [data]);
  const hasActiveFilters = filters.severity.length > 0 || filters.category.length > 0 || filters.from || filters.to || filters.unread_only;

  return (
    <div className="space-y-4" data-testid="alerts-page">
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <CardTitle className="text-base inline-flex items-center gap-2"><Bell size={16} /> Alerts Center</CardTitle>
              <CardDescription>
                Full backlog of every alert raised by RAPTOR — overdue surveys, freshly critical
                sites, and delivery failures. Use the filters to triage the queue.
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => nav('/insurer/settings/notifications')}
                      data-testid="alerts-prefs-link">
                <Settings2 size={14} /> Preferences
              </Button>
              <Button variant="default" size="sm" onClick={() => void markAll()}
                      disabled={!data?.unread_count} data-testid="alerts-mark-all">
                <Check size={14} /> Mark all read
              </Button>
              <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}
                      data-testid="alerts-refresh">
                <RefreshCw size={14} /> {loading ? 'Refreshing…' : 'Refresh'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-4 gap-3 items-end">
            <div className="space-y-1.5">
              <Label>Severity</Label>
              <div className="flex flex-wrap gap-1.5">
                {SEVERITIES.map(s => (
                  <button
                    key={s.key} type="button"
                    onClick={() => toggleValue('severity', s.key)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                      filters.severity.includes(s.key)
                        ? 'bg-raptor-blue/15 border-raptor-blue/40 text-raptor-navy'
                        : 'bg-white border-border/60 text-raptor-navy/70 hover:bg-raptor-neutral/40'
                    }`}
                    data-testid={`alerts-filter-sev-${s.key}`}
                  >{s.label}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Category</Label>
              <div className="flex flex-wrap gap-1.5">
                {CATEGORIES.map(c => (
                  <button
                    key={c.key} type="button"
                    onClick={() => toggleValue('category', c.key)}
                    className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${
                      filters.category.includes(c.key)
                        ? 'bg-raptor-blue/15 border-raptor-blue/40 text-raptor-navy'
                        : 'bg-white border-border/60 text-raptor-navy/70 hover:bg-raptor-neutral/40'
                    }`}
                    data-testid={`alerts-filter-cat-${c.key}`}
                  >{c.label}</button>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>From</Label>
              <Input type="date" value={filters.from}
                     onChange={(e) => { setPage(0); setFilters({ ...filters, from: e.target.value }); }}
                     data-testid="alerts-filter-from" />
            </div>
            <div className="space-y-1.5">
              <Label>To</Label>
              <div className="flex gap-2">
                <Input type="date" value={filters.to}
                       onChange={(e) => { setPage(0); setFilters({ ...filters, to: e.target.value }); }}
                       data-testid="alerts-filter-to" />
                <Select value={filters.unread_only ? '1' : '0'}
                        onChange={(e) => { setPage(0); setFilters({ ...filters, unread_only: e.target.value === '1' }); }}
                        data-testid="alerts-filter-status" className="w-32">
                  <option value="0">All</option>
                  <option value="1">Unread only</option>
                </Select>
              </div>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="mt-2">
              <Button size="sm" variant="ghost" data-testid="alerts-clear-filters"
                      onClick={() => { setPage(0); setFilters({ severity: [], category: [], from: '', to: '', unread_only: false }); }}>
                <X size={12} /> Clear filters
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-xs font-body">
            <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70">
              <tr>
                <th className="py-2 px-3 text-left w-8"> </th>
                <th className="py-2 px-3 text-left">When</th>
                <th className="py-2 px-3 text-left">Severity</th>
                <th className="py-2 px-3 text-left">Category</th>
                <th className="py-2 px-3 text-left">Title</th>
                <th className="py-2 px-3 text-left">Details</th>
                <th className="py-2 px-3 text-right w-24">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(!data || data.items.length === 0) && (
                <tr><td colSpan={7} className="py-8 text-center text-raptor-navy/50">
                  {loading ? 'Loading…' : 'No alerts match this view.'}
                </td></tr>
              )}
              {(data?.items || []).map((n) => (
                <tr
                  key={n.id}
                  className={`border-t border-border/40 cursor-pointer transition-colors ${
                    n.read ? 'bg-white hover:bg-raptor-neutral/40' : 'bg-raptor-blue/[0.04] hover:bg-raptor-blue/[0.08]'
                  }`}
                  onClick={() => void openAlert(n)}
                  data-testid={`alerts-row-${n.id}`}
                >
                  <td className="py-2 px-3">
                    {!n.read && <span className="inline-block w-2 h-2 rounded-full bg-raptor-blue" title="Unread" />}
                  </td>
                  <td className="py-2 px-3 font-tabular text-raptor-navy/70">{timeFmt(n.created_at)}</td>
                  <td className="py-2 px-3">{severityBadge(n.severity)}</td>
                  <td className="py-2 px-3">
                    <span className="inline-flex items-center gap-1.5 text-raptor-navy/70">
                      {categoryIcon(n.category)} {n.category || '—'}
                    </span>
                  </td>
                  <td className={`py-2 px-3 ${n.read ? 'text-raptor-navy/80' : 'font-semibold text-raptor-navy'}`}>
                    {n.title}
                  </td>
                  <td className="py-2 px-3 text-raptor-navy/60 max-w-md truncate">{n.body}</td>
                  <td className="py-2 px-3 text-right space-x-1" onClick={(e) => e.stopPropagation()}>
                    {n.category === 'unknown_abm' && n.target_ref?.matrix_id && (
                      <Button size="sm" variant="outline"
                              onClick={() => setAutofix(n)}
                              data-testid={`alerts-row-autofix-${n.id}`}
                              title="Add to ABM library">
                        <PackagePlus size={13} /> Add to library
                      </Button>
                    )}
                    {!n.read && (
                      <Button size="sm" variant="ghost" onClick={() => void markOne(n.id)}
                              data-testid={`alerts-row-mark-${n.id}`} aria-label="Mark as read">
                        <Check size={13} />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => void deleteOne(n.id)}
                            data-testid={`alerts-row-delete-${n.id}`} aria-label="Delete alert">
                      <Trash2 size={13} className="text-red-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-between px-1">
          <span className="text-xs text-raptor-navy/60 font-body">
            Showing <span className="font-tabular">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, data.total)}</span>
            {' '}of <span className="font-tabular">{data.total}</span>
          </span>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="outline" onClick={() => setPage(Math.max(0, page - 1))}
                    disabled={page === 0} data-testid="alerts-prev">Previous</Button>
            <span className="text-xs text-raptor-navy/60 font-tabular px-2">
              {page + 1} / {totalPages}
            </span>
            <Button size="sm" variant="outline" onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                    disabled={page >= totalPages - 1} data-testid="alerts-next">Next</Button>
          </div>
        </div>
      )}

      {autofix && (
        <UnknownAbmAutofix
          alert={autofix}
          onClose={() => setAutofix(null)}
          onFixed={() => { setAutofix(null); void load(); }}
        />
      )}
    </div>
  );
};

export default AlertsPage;
export { AlertsPage };

/* ═══════════════════════════════════════════════════════════════════════
 * UnknownAbmAutofix — one-click "Add to library" for unknown_abm alerts.
 *
 * The alert's ``target_ref`` carries ``matrix_id`` + ``unknown_types[]``.
 * We render one editable row per unknown type with sane defaults (name
 * verbatim, group=best-guess, type=notes, capacity blank) so the
 * underwriter can adjust and hit Add. Successful adds mark the alert
 * read; a "Skip & mark read" escape hatch is offered too.
 * ═══════════════════════════════════════════════════════════════════════ */
interface AutofixRow {
  new_name: string; old_name: string; group: string;
  type: 'notes' | 'coin'; capacity: number | ''; added: boolean;
}
function guessGroup(name: string): string {
  const m = name.match(/protector\s*[\d\s]+/i);
  if (m) return m[0].trim().replace(/\s+/g, ' ');
  const first = name.split(/[\s(]/)[0];
  return first ? first.trim() : '';
}

const UnknownAbmAutofix: React.FC<{ alert: AlertRow; onClose: () => void; onFixed: () => void }> = ({ alert, onClose, onFixed }) => {
  const matrixId = alert.target_ref?.matrix_id as string | undefined;
  const initial = React.useMemo<AutofixRow[]>(
    () => (alert.target_ref?.unknown_types || []).map((n) => ({
      new_name: n, old_name: '', group: guessGroup(n),
      type: /coin/i.test(n) ? 'coin' : 'notes',
      capacity: '', added: false,
    })),
    [alert.target_ref],
  );
  const [rows, setRows] = React.useState<AutofixRow[]>(initial);
  const [busy, setBusy] = React.useState<number | null>(null);
  const setField = <K extends keyof AutofixRow>(idx: number, k: K, v: AutofixRow[K]) =>
    setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));

  const addOne = React.useCallback(async (idx: number) => {
    if (!matrixId) return;
    const r = rows[idx];
    if (!r.new_name.trim()) { toast.error('New name is required'); return; }
    setBusy(idx);
    try {
      await api.post(`/risk/matrices/${matrixId}/abm-library`, {
        new_name: r.new_name.trim(),
        old_name: r.old_name.trim(),
        group: r.group.trim(),
        type: r.type,
        capacity: Number(r.capacity || 0),
      });
      toast.success(`Added "${r.new_name}" to the library`);
      setField(idx, 'added', true);
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      toast.error(detail ?? 'Add failed');
    } finally { setBusy(null); }
  }, [matrixId, rows]);

  const finishAndRescore = React.useCallback(async () => {
    // Mark alert read + rescore all assessments so KPIs move.
    try { await api.post(`/notifications/${alert.id}/read`); } catch { /* ignore */ }
    if (matrixId) {
      try {
        const r = await api.post<{ ok: boolean; rescored: number }>(`/risk/matrices/${matrixId}/abm-library/rescore`, {});
        toast.success(`Re-scored ${r.data.rescored} assessment${r.data.rescored === 1 ? '' : 's'}`);
      } catch { toast.error('Re-score failed'); }
    }
    onFixed();
  }, [alert.id, matrixId, onFixed]);

  const skipAndClose = React.useCallback(async () => {
    try { await api.post(`/notifications/${alert.id}/read`); } catch { /* ignore */ }
    onFixed();
  }, [alert.id, onFixed]);

  const allAdded = rows.length > 0 && rows.every((r) => r.added);

  return (
    <div className="fixed inset-0 z-[300] bg-raptor-navy/40 backdrop-blur-sm flex items-center justify-center p-4"
         onClick={onClose} data-testid="alerts-autofix-dialog">
      <Card className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle className="inline-flex items-center gap-2"><PackagePlus size={16} /> Add missing ABM(s) to library</CardTitle>
          <div className="text-xs text-raptor-navy/70 mt-1 font-body">
            {rows.length} model{rows.length === 1 ? '' : 's'} not recognised by the matrix.
            Adjust the details and click <b>Add</b> per row. Then <b>Finish &amp; re-score</b> flows
            the fix through every existing assessment.
          </div>
        </CardHeader>
        <CardContent className="p-0 max-h-[60vh] overflow-y-auto">
          <table className="w-full text-xs font-body">
            <thead className="bg-raptor-neutral/40 text-[10px] uppercase text-raptor-navy/70">
              <tr>
                <th className="py-1.5 px-2 text-left">New name *</th>
                <th className="py-1.5 px-2 text-left">Old / legacy</th>
                <th className="py-1.5 px-2 text-left">Group</th>
                <th className="py-1.5 px-2 text-left">Type</th>
                <th className="py-1.5 px-2 text-right">Capacity</th>
                <th className="py-1.5 px-2 text-right w-28"> </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} className={`border-t border-border/40 ${r.added ? 'opacity-50' : ''}`}>
                  <td className="py-1.5 px-2"><Input className="h-7 text-xs" value={r.new_name} disabled={r.added} onChange={(e) => setField(i, 'new_name', e.target.value)} /></td>
                  <td className="py-1.5 px-2"><Input className="h-7 text-xs" value={r.old_name} disabled={r.added} onChange={(e) => setField(i, 'old_name', e.target.value)} /></td>
                  <td className="py-1.5 px-2"><Input className="h-7 text-xs" value={r.group} disabled={r.added} onChange={(e) => setField(i, 'group', e.target.value)} /></td>
                  <td className="py-1.5 px-2">
                    <Select className="h-7 text-xs" value={r.type} disabled={r.added}
                            onChange={(e) => setField(i, 'type', e.target.value as 'notes' | 'coin')}>
                      <option value="notes">Notes</option>
                      <option value="coin">Coin</option>
                    </Select>
                  </td>
                  <td className="py-1.5 px-2"><Input type="number" min={0} className="h-7 text-xs text-right" value={r.capacity} disabled={r.added} onChange={(e) => setField(i, 'capacity', e.target.value === '' ? '' : Number(e.target.value))} /></td>
                  <td className="py-1.5 px-2 text-right">
                    {r.added ? (
                      <Badge className="bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"><Check size={11} /> Added</Badge>
                    ) : (
                      <Button size="sm" variant="default" onClick={() => void addOne(i)}
                              disabled={busy === i} data-testid={`autofix-add-${i}`}>
                        {busy === i ? 'Adding…' : 'Add'}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
        <div className="border-t border-border/60 px-4 py-3 flex items-center justify-between gap-2 flex-wrap">
          <Button variant="ghost" onClick={() => void skipAndClose()} data-testid="autofix-skip">
            Skip &amp; mark read
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} data-testid="autofix-close">Close</Button>
            <Button variant="default" onClick={() => void finishAndRescore()}
                    disabled={!allAdded} data-testid="autofix-finish">
              <Save size={13} /> Finish &amp; re-score
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
